import { useCallback, useEffect, useRef, useState } from "react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import { type Point } from "@/lib/geometry";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import { toast } from "sonner";
import { type MeasurementType, type ToolType } from "@/viewport-tools/measurement";

import {
  type InteractionState,
  type MeasurementOverlayContextMenuState,
} from "./interactions/types";
import {
  type DraftMeasurementState,
  type MeasurementAnnotation,
  type MeasurementGeometry,
  type MeasurementOverlayRecord,
  type PolygonGeometry,
} from "./measurement-overlay-types";
import { useMeasurementOverlayGlobalShortcuts } from "./interactions/useMeasurementOverlayGlobalShortcuts";
import { useMeasurementOverlayGeometryHelpers } from "./interactions/useMeasurementOverlayGeometryHelpers";
import { useMeasurementOverlayTransformActions } from "./interactions/useMeasurementOverlayTransformActions";
import { useMeasurementOverlayStageHandlers } from "./interactions/useMeasurementOverlayStageHandlers";

interface UseMeasurementOverlayInteractionsArgs {
  viewport: DocumentViewportValue;
  scale: MeasurementScale | null;
  activeTool: ToolType | null;
  activeMeasurementType: MeasurementType | null;
  currentHeightFeet?: number;
  snapEnabled: boolean;
  incrementSnapEnabled: boolean;
  incrementStepFeet: number;
  measurements: MeasurementOverlayRecord[];
  draftMeasurement: DraftMeasurementState | null;
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  activeMeasurementId: Id<"takeoffPilotMeasurements"> | null;
  canPasteMeasurement?: boolean;
  onDraftMeasurementChange: (draft: DraftMeasurementState | null) => void;
  onSaveDraftMeasurement: () => Promise<void>;
  onCancelDraftMeasurement: () => void;
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements"> | null,
    options?: { shiftKey?: boolean }
  ) => void;
  onUpdateMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry,
    height: number | undefined,
    value: number
  ) => Promise<void>;
  onUpdateMeasurementAnnotation?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => Promise<void>;
  onCopyMeasurement?: (measurementId: Id<"takeoffPilotMeasurements">) => void;
  onCopyMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => void;
  onPasteMeasurement?: (point?: Point) => Promise<void> | void;
  onDuplicateMeasurement?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    point?: Point
  ) => Promise<void> | void;
  onDuplicateMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    point?: Point
  ) => Promise<void> | void;
}

export function useMeasurementOverlayInteractions({
  viewport,
  scale,
  activeTool,
  activeMeasurementType,
  currentHeightFeet,
  snapEnabled,
  incrementSnapEnabled,
  incrementStepFeet,
  measurements,
  draftMeasurement,
  selectedMeasurementIds,
  activeMeasurementId,
  canPasteMeasurement = false,
  onDraftMeasurementChange,
  onSaveDraftMeasurement,
  onCancelDraftMeasurement,
  onSelectMeasurement,
  onUpdateMeasurement,
  onUpdateMeasurementAnnotation,
  onCopyMeasurement,
  onCopyMeasurements,
  onPasteMeasurement,
  onDuplicateMeasurement,
  onDuplicateMeasurements,
}: UseMeasurementOverlayInteractionsArgs) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const interactionRef = useRef<InteractionState | null>(null);
  const previousActiveToolRef = useRef<ToolType | null>(activeTool);
  const latestDraftRef = useRef<DraftMeasurementState | null>(draftMeasurement);
  const drawingDraftRef = useRef<DraftMeasurementState | null>(null);
  const lastPointerPagePointRef = useRef<Point | null>(null);
  const isSpacePressedRef = useRef(false);
  const prevDraftMeasurementRef = useRef(draftMeasurement);

  const [interaction, setInteraction] = useState<InteractionState | null>(null);
  const [drawingDraft, setDrawingDraft] = useState<DraftMeasurementState | null>(null);
  const [previewGeometries, setPreviewGeometries] = useState<
    Record<string, MeasurementGeometry | PolygonGeometry>
  >({});
  const [previewAnnotations, setPreviewAnnotations] = useState<Record<string, MeasurementAnnotation>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<MeasurementOverlayContextMenuState | null>(
    null
  );

  const setContextMenuOpen = useCallback((open: boolean) => {
    if (!open) {
      setContextMenu(null);
    }
  }, []);

  useEffect(() => {
    if (previousActiveToolRef.current === activeTool) {
      return;
    }
    previousActiveToolRef.current = activeTool;

    interactionRef.current = null;
    drawingDraftRef.current = null;
    setInteraction(null);
    setDrawingDraft(null);
    setPreviewGeometries({});
    setPreviewAnnotations({});
    setErrorMessage(null);
    setContextMenu(null);
  }, [activeTool]);

  useEffect(() => {
    if (draftMeasurement !== null) {
      latestDraftRef.current = draftMeasurement;
    } else if (prevDraftMeasurementRef.current !== null && draftMeasurement === null) {
      latestDraftRef.current = null;
      drawingDraftRef.current = null;
      setDrawingDraft(null);
    }
    prevDraftMeasurementRef.current = draftMeasurement;
  }, [draftMeasurement]);

  const clearLocalDraft = useCallback(() => {
    latestDraftRef.current = null;
    drawingDraftRef.current = null;
    setDrawingDraft(null);
  }, []);

  const handleCancelDraft = useCallback(() => {
    clearLocalDraft();
    setErrorMessage(null);
    onCancelDraftMeasurement();
  }, [clearLocalDraft, onCancelDraftMeasurement]);

  const handleSaveDraft = useCallback(async () => {
    try {
      await onSaveDraftMeasurement();
      clearLocalDraft();
      setErrorMessage(null);
      onCancelDraftMeasurement();
    } catch (error) {
      const message = getUserFacingErrorMessage(error, "Failed to save measurement");
      setErrorMessage(message);
      toast.error(message);
    }
  }, [clearLocalDraft, onCancelDraftMeasurement, onSaveDraftMeasurement]);

  const clearSelection = useCallback(() => {
    onSelectMeasurement(null);
    interactionRef.current = null;
    setInteraction(null);
    setPreviewGeometries({});
    setPreviewAnnotations({});
    setContextMenu(null);
  }, [onSelectMeasurement]);

  const cancelDrawingSession = useCallback(() => {
    interactionRef.current = null;
    drawingDraftRef.current = null;
    setInteraction(null);
    setDrawingDraft(null);
    setPreviewGeometries({});
    setPreviewAnnotations({});
    setErrorMessage(null);
    setContextMenu(null);
    handleCancelDraft();
  }, [handleCancelDraft]);

  useMeasurementOverlayGlobalShortcuts({
    interactionRef,
    drawingDraftRef,
    latestDraftRef,
    isSpacePressedRef,
    lastPointerPagePointRef,
    selectedMeasurementIds,
    activeMeasurementId,
    canPasteMeasurement,
    contextMenuOpen: contextMenu !== null,
    clearSelection,
    cancelDrawingSession,
    setContextMenuOpen,
    onCopyMeasurement,
    onCopyMeasurements,
    onPasteMeasurement,
    onDuplicateMeasurement,
    onDuplicateMeasurements,
  });

  const {
    getPagePoint,
    getScreenDistance,
    getActiveHeight,
    snapSegmentPoint,
    snapRectangleCorner,
  } = useMeasurementOverlayGeometryHelpers({
    viewport,
    overlayRef,
    latestDraftRef,
    currentHeightFeet,
    incrementSnapEnabled,
    incrementStepFeet,
    scale,
  });

  const updateUnsavedDraft = useCallback(
    (nextDraft: DraftMeasurementState) => {
      latestDraftRef.current = nextDraft;
      if (drawingDraftRef.current) {
        drawingDraftRef.current = nextDraft;
        setDrawingDraft(nextDraft);
        return;
      }
      onDraftMeasurementChange(nextDraft);
    },
    [onDraftMeasurementChange]
  );

  const {
    startDraftMove,
    openContextMenu,
    startMeasurementMove,
    startMeasurementResize,
    startMeasurementVertexDrag,
    openVertexContextMenu,
    startMeasurementLabelMove,
  } = useMeasurementOverlayTransformActions({
    getPagePoint,
    overlayRef,
    lastPointerPagePointRef,
    interactionRef,
    drawingDraftRef,
    measurements,
    selectedMeasurementIds,
    handleCancelDraft,
    onSelectMeasurement,
    setInteraction,
    setContextMenu,
    setPreviewGeometries,
  });

  const {
    handleStageMouseDown,
    handleStageContextMenu,
    handleStageMouseMove,
    handleStageMouseUp,
    handleStageMouseLeave,
  } = useMeasurementOverlayStageHandlers({
    viewport,
    scale,
    activeTool,
    activeMeasurementType,
    snapEnabled,
    incrementSnapEnabled,
    measurements,
    selectedMeasurementIds,
    previewAnnotations,
    previewGeometries,
    interaction,
    interactionRef,
    drawingDraftRef,
    latestDraftRef,
    lastPointerPagePointRef,
    isSpacePressedRef,
    getPagePoint,
    getScreenDistance,
    getActiveHeight,
    snapSegmentPoint,
    snapRectangleCorner,
    updateUnsavedDraft,
    onDraftMeasurementChange,
    onSelectMeasurement,
    onUpdateMeasurement,
    onUpdateMeasurementAnnotation,
    handleCancelDraft,
    clearSelection,
    setInteraction,
    setContextMenu,
    setDrawingDraft,
    setPreviewAnnotations,
    setPreviewGeometries,
    setErrorMessage,
  });

  return {
    overlayRef,
    interaction,
    drawingDraft,
    previewAnnotations,
    previewGeometries,
    errorMessage,
    contextMenu,
    latestDraftMeasurement: latestDraftRef.current,
    handleCancelDraft,
    handleSaveDraft,
    handleStageMouseDown,
    handleStageContextMenu,
    handleStageMouseMove,
    handleStageMouseUp,
    handleStageMouseLeave,
    startDraftMove,
    openContextMenu,
    startMeasurementMove,
    startMeasurementResize,
    startMeasurementVertexDrag,
    openVertexContextMenu,
    startMeasurementLabelMove,
    closeContextMenu: () => setContextMenu(null),
    lastPointerPagePoint: lastPointerPagePointRef.current,
  };
}

export type {
  InteractionState,
  MeasurementOverlayContextMenuState,
  ResizeHandle,
} from "./interactions/types";
